"use client";

/**
 * EoaSweepInline — compact inline notice for /settings?tab=wallet, shown
 * inside the Polymarket wallet card. Surfaces when the user has USDC.e /
 * USDC / pUSD stranded on their Privy login EOA and offers a one-tap
 * "send to trading wallet" that drives the same flow as the floating
 * EoaSweepBanner (shared via useEoaSweep). Unlike the banner this is
 * persistent — it doesn't dismiss, so users who closed the banner can
 * still recover funds from settings.
 *
 * Renders nothing when there's nothing to move (needsMove false).
 */

import { FiAlertTriangle } from "react-icons/fi";
import { useEoaSweep } from "@/hooks/useEoaSweep";
import { useT } from "@/hooks/useT";

export default function EoaSweepInline() {
  const t = useT();
  const { needsMove, balances, depositWallet, eoa, running, swept, move } = useEoaSweep();

  // `swept` hides the notice the instant a successful move clears every
  // token, so the user isn't left staring at a button for funds that
  // already left their EOA.
  if (!needsMove || swept || !balances || !depositWallet || !eoa) return null;

  // Per-token chips so the user sees exactly what's stranded, not just a
  // rolled-up dollar figure (pUSD vs USDC.e matters — pUSD can hit the
  // Polymarket allow-list and need manual support).
  const parts: string[] = [];
  if (balances.usdce > 0) parts.push(`${balances.usdce.toFixed(2)} USDC.e`);
  if (balances.usdc > 0) parts.push(`${balances.usdc.toFixed(2)} USDC`);
  if (balances.pusd > 0) parts.push(`${balances.pusd.toFixed(2)} pUSD`);

  return (
    <div className="mt-3 rounded-[5px] border border-amber-300/60 bg-amber-50 dark:border-amber-300/20 dark:bg-amber-400/10 p-3">
      <div className="flex items-start gap-2">
        <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 leading-snug">
            {t("settings.wallet.eoaSweep.title", { amount: balances.totalUsd.toFixed(2) })}
          </p>
          <p className="text-[11px] text-amber-700/80 dark:text-amber-200/80 leading-snug mt-0.5">
            {t("settings.wallet.eoaSweep.body", { tokens: parts.join(" · ") })}
          </p>
        </div>
      </div>
      <button
        onClick={move}
        disabled={running}
        className="mt-2.5 w-full px-3 py-2 rounded-[5px] bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {running ? t("settings.wallet.eoaSweep.sending") : t("settings.wallet.eoaSweep.button")}
      </button>
    </div>
  );
}
